import React, { useMemo, useCallback } from 'react';
import { Calendar } from 'primereact/calendar';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../utils/dateUtils';
import { Dropdown } from 'primereact/dropdown';
import StickyScrollTable from '../../../../components/common/StickyScrollTable';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';

const TEXT_FIELDS = ['warrClaimsNo', 'islemYapan', 'bayiAdi', 'bayiKodu', 'gunFarki'];
const DATE_FIELDS = ['islemTarihi'];
const DROPDOWN_FIELDS = ['islem'];

const getIslemSeverity = (islem) => {
    if (!islem) return 'secondary';
    const lower = islem.toLowerCase();
    if (lower.includes('onaylandi')) return 'success';
    if (lower.includes('reddedildi')) return 'danger';
    if (lower.includes('taslak')) return 'secondary';
    if (lower.includes('ilk talep')) return 'info';
    if (lower.includes('return')) return 'warning';
    if (lower.includes('onay')) return 'info';
    return 'secondary';
};

const ClaimHistoryReportTable = ({ items, totalCount, loading, lazyParams, onPage, activeFilters, onFiltersChange, sortField, sortOrder, onSortChange }) => {
    const { th } = useLanguage();
    const translateIslem = useCallback(
        (islem) => {
            const key = getIslemTranslationKey(islem);
            return key ? th(key) : islem || '-';
        },
        [th]
    );
    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const islemOptions = [
        { label: th('claimHistoryReport.values.taslak'), value: 'Taslak' },
        { label: th('claimHistoryReport.values.ilkTalep'), value: 'ilk talep' },
        { label: th('claimHistoryReport.values.returnToService'), value: 'Return to SERVICE' },
        { label: th('claimHistoryReport.values.returnToKarsan'), value: 'Return to KARSAN' },
        { label: th('claimHistoryReport.values.onay'), value: 'Onay' },
        { label: th('claimHistoryReport.values.onaylandi'), value: 'Onaylandi' },
        { label: th('claimHistoryReport.values.reddedildi'), value: 'Reddedildi' }
    ];

    const islemFilterTemplate = (options) => <Dropdown value={options.value} options={islemOptions} onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('common.select')} className="p-column-filter" showClear />;

    const tableFilters = useMemo(
        () => ({
            warrClaimsNo: { value: activeFilters?.warrClaimsNo ?? null, matchMode: activeFilters?.warrClaimsNoMatchMode ?? FilterMatchMode.CONTAINS },
            islem: { value: activeFilters?.islem ?? null, matchMode: activeFilters?.islemMatchMode ?? FilterMatchMode.CONTAINS },
            islemYapan: { value: activeFilters?.islemYapan ?? null, matchMode: activeFilters?.islemYapanMatchMode ?? FilterMatchMode.CONTAINS },
            bayiAdi: { value: activeFilters?.bayiAdi ?? null, matchMode: activeFilters?.bayiAdiMatchMode ?? FilterMatchMode.CONTAINS },
            bayiKodu: { value: activeFilters?.bayiKodu ?? null, matchMode: activeFilters?.bayiKoduMatchMode ?? FilterMatchMode.CONTAINS },
            islemTarihi: { value: activeFilters?.islemTarihi ?? null, matchMode: activeFilters?.islemTarihiMatchMode ?? FilterMatchMode.DATE_IS },
            gunFarki: { value: activeFilters?.gunFarki ?? null, matchMode: activeFilters?.gunFarkiMatchMode ?? FilterMatchMode.EQUALS }
        }),
        [activeFilters]
    );

    const getIslemTranslationKey = (islem) => {
        if (!islem) return null;

        switch (islem) {
            case 'Taslak':
                return 'claimHistoryReport.values.taslak';
            case 'ilk talep':
                return 'claimHistoryReport.values.ilkTalep';
            case 'Return to SERVICE':
                return 'claimHistoryReport.values.returnToService';
            case 'Return to KARSAN':
                return 'claimHistoryReport.values.returnToKarsan';
            case 'Onay':
                return 'claimHistoryReport.values.onay';
            case 'Onaylandi':
                return 'claimHistoryReport.values.onaylandi';
            case 'Reddedildi':
                return 'claimHistoryReport.values.reddedildi';
            default:
                return null;
        }
    };

    const handleTableFilter = useCallback(
        (e) => {
            const filters = e?.filters || {};
            const updated = { ...(activeFilters || {}) };

            TEXT_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
                }
            });

            DATE_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.DATE_IS;
                }
            });

            // Dropdown — matchMode sabit Equals
            DROPDOWN_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = FilterMatchMode.EQUALS;
                }
            });

            onFiltersChange?.(updated);
        },
        [activeFilters, onFiltersChange]
    );

    const dateFilterTemplate = (options) => {
        const handleDateChange = (e) => {
            let d = e.value;
            if (d instanceof Date) d = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            options.filterApplyCallback(d);
        };
        return <Calendar value={options.value} onChange={handleDateChange} dateFormat="dd.mm.yy" placeholder={th('common.search')} showIcon showButtonBar className="p-column-filter" style={{ minWidth: '12rem' }} />;
    };

    const makeTextColumn = (field, header, options = {}) => {
        const { body, sortable = true, minWidth = '150px' } = options;
        return {
            field,
            columnKey: field,
            sortable,
            header,
            style: { minWidth },
            body,
            filter: true,
            filterField: field,
            filterPlaceholder: th('common.search'),
            showFilterMenu: true,
            filterMatchModeOptions: textMatchModeOptions,
            showClearButton: false
        };
    };

    const tableItems = (items ?? []).map((item, index) => ({
        ...item,
        rowKey: `${item.warrClaimsNo}-${item.islemTarihi}-${index}`
    }));

    const columns = [
        makeTextColumn('warrClaimsNo', th('claimHistoryReport.columns.warrClaimsNo'), { minWidth: '140px' }),
        {
            field: 'islem',
            columnKey: 'islem',
            header: th('claimHistoryReport.columns.islem'),
            sortable: true,
            style: { minWidth: '160px' },
            body: (row) => translateIslem(row.islem),
            filter: true,
            filterField: 'islem',
            filterElement: islemFilterTemplate,
            showFilterMenu: false,
            showClearButton: false
        },
        makeTextColumn('islemYapan', th('claimHistoryReport.columns.islemYapan'), { minWidth: '180px' }),
        {
            field: 'islemTarihi',
            columnKey: 'islemTarihi',
            header: th('claimHistoryReport.columns.islemTarihi'),
            sortable: true,
            style: { minWidth: '180px' },
            body: (row) => formatDateOnly(row.islemTarihi),
            filter: true,
            filterField: 'islemTarihi',
            filterElement: dateFilterTemplate,
            showFilterMenu: false,
            showClearButton: false
        },
        makeTextColumn('bayiAdi', th('claimHistoryReport.columns.bayiAdi'), { minWidth: '200px' }),
        makeTextColumn('bayiKodu', th('claimHistoryReport.columns.bayiKodu'), { minWidth: '120px' }),
        {
            field: 'gunFarki',
            columnKey: 'gunFarki',
            header: th('claimHistoryReport.columns.gunFarki'),
            sortable: true,
            style: { minWidth: '110px' },
            body: (row) =>
                row.gunFarki != null ? (
                    <span className="font-semibold">
                        {row.gunFarki} {th('claimHistoryReport.days')}
                    </span>
                ) : (
                    <span className="text-color-secondary">—</span>
                ),
            filter: true,
            filterField: 'gunFarki',
            filterPlaceholder: th('common.search'),
            showFilterMenu: true,
            filterMatchModeOptions: textMatchModeOptions,
            showClearButton: false
        }
    ];

    return (
        <StickyScrollTable deps={[tableItems, activeFilters]} scrollBarPosition="top">
        <PreferencesDataTable
            tableKey="claim-history-report"
            columns={columns}
            value={tableItems}
            totalRecords={totalCount}
            className="p-datatable-gridlines"
            showGridlines
            lazy
            paginator
            first={lazyParams.first}
            rows={lazyParams.rows || 20}
            onPage={onPage}
            dataKey="rowKey"
            loading={loading}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
            emptyMessage={th('common.noRecords')}
            rowsPerPageOptions={[10, 20, 50, 100]}
            tableStyle={{ minWidth: '80rem' }}
            onSort={(e) => onSortChange?.(e)}
            sortField={sortField}
            sortOrder={sortOrder}
            sortMode="single"
            pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
        />
        </StickyScrollTable>
    );
};

export default ClaimHistoryReportTable;
